import {
  IAddEmployeeForm,
  IEmployeeOnBoardResponse,
  IsalaryDetail,
  ISalaryPageStates,
} from '@/interfaces/employee.model';
import { Controller, useFormContext } from 'react-hook-form';
import { Spinner } from 'reactstrap';
import ErrorMessage from './ErrorMessage';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import PrimaryInput from '../ui/PrimaryInput';
import Button from '../ui/Button';

interface ISalaryFieldsProps {
  isLoadingUserSalary?: boolean;
  salary: IsalaryDetail;
  isLoadingSalaryCreation: boolean;
  empId: string | null;
  setPageStates: React.Dispatch<React.SetStateAction<ISalaryPageStates>>;
  getEmployeeDetail: () => Promise<void>;
}
const SalaryFields = ({
  isLoadingUserSalary,
  salary,
  isLoadingSalaryCreation,
  empId,
  setPageStates,
  getEmployeeDetail,
}: ISalaryFieldsProps) => {
  const {
    register,
    trigger,
    getValues,
    control,
    formState: { errors },
  } = useFormContext<IAddEmployeeForm>();
  const handleCreateSalary = async () => {
    const isValid = await trigger(['userSalary']);
    if (isValid && empId) {
      setPageStates((prev) => ({ ...prev, isLoadingSalaryCreation: true }));
      const data = getValues();
      const userSalary = data?.userSalary;
      try {
        const response: IEmployeeOnBoardResponse = await callApi(
          API_URL + API_ENDPOINT.createUserSalary + `?userId=${empId}`,
          { method: 'POST', data: { userSalary } },
        );
        if (response?.status) {
          showToastNotification('success', 'Employee Salary Created');
          getEmployeeDetail();
        }
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error) {
          showToastNotification('danger', (error as IApiErrorResponse).message);
        } else {
          showToastNotification('danger', 'Something went wrong');
        }
      } finally {
        setPageStates((prev) => ({ ...prev, isLoadingSalaryCreation: false }));
      }
    }
  };
  return (
    <div className="d-flex justify-content-center align-items-start mt-5">
      <div className="card border-0" style={{ backgroundColor: '#f9fafd' }}>
        <div className="px-4 py-3 aborder-bottom d-flex align-items-center bg-white rounded-top-4">
          <i className="bi bi-wallet2 fs-4 me-2 text-primary"></i>
          <h4 className="mb-0 text-primary">Set Salary Details</h4>
        </div>
        {isLoadingUserSalary ? (
          <div className="d-flex justify-content-center my-5">
            <Spinner />
          </div>
        ) : (
          <div className="px-4 py-4">
            <div className="row g-4">
              {salary?.siteSalaryHeadDetail?.length > 0 &&
                salary?.siteSalaryHeadDetail
                  ?.filter((i) => i?.salaryhead?.takeInput === true)
                  ?.map((item, index: number) => {
                    const error = errors?.userSalary?.[index]?.amount;
                    const isEarning = item?.salaryhead?.type === 'earning';

                    return (
                      <div className="col-md-6" key={item?.salaryhead?.id}>
                        <div className="d-flex align-items-start border rounded-3 bg-white shadow-sm p-3 position-relative">
                          <div className="w-100">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong className="text-dark">
                                {item?.salaryhead?.name}
                              </strong>
                              <span
                                className={`badge text-capitalize bg-${
                                  isEarning ? 'success-subtle' : 'danger-subtle'
                                } text-${
                                  isEarning ? 'success' : 'danger'
                                } fw-semibold`}
                              >
                                {item?.salaryhead?.type}
                              </span>
                            </div>
                            <Controller
                              control={control}
                              name={`userSalary.${index}.amount`}
                              defaultValue={item?.amount || ''}
                              render={({ field }) => (
                                <PrimaryInput
                                  {...field}
                                  label="Amount"
                                  type="text"
                                  value={field.value.toString() || ''}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /\D/g,
                                      '',
                                    );
                                    field.onChange(value);
                                  }}
                                  className={`form-control ${error ? 'is-invalid' : ''}`}
                                  placeholder="Enter amount"
                                />
                              )}
                            />
                            {errors?.userSalary && (
                              <ErrorMessage
                                message={`Please enter a valid amount for
                                ${item?.salaryhead?.name}.`}
                              />
                            )}
                            <input
                              type="hidden"
                              {...register(
                                `userSalary.${index}.siteSalaryHeadId`,
                              )}
                              value={item?.id}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
              <div className="text-center border-top p-3 bg-white rounded-bottom-4">
                <Button
                  type="button"
                  className="btn btn-primary px-5"
                  disabled={isLoadingSalaryCreation}
                  onClick={handleCreateSalary}
                >
                  {isLoadingSalaryCreation ? <Spinner /> : 'Submit'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default SalaryFields;
